import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import './ModalStyle.scss'
import { STATUSES, MODES } from '../../lib/constants'
import {
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { useTranslation } from 'react-i18next'
import { AutocompleteTextField } from './CustomerStyledComponents'
import { CustomDialogTitle } from './CustomerStyledComponents'
import MaterialsList from './MaterialsList'
import { SelectStatus } from '../../components'
import { orderStatuses } from '../../lib/statuses'
import DeliveryDateTimePicker from './DeliveryDateTimePicker'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import {
  gqlAddressAutoFill,
  gqlClients,
  gqlMaterials,
  gqlUsers,
  gqlCommand,
  gqlConstructionSite,
  gqlOrderDelivery,
} from '../../gql'
import { getUserFromResponse } from '../../lib/helpers'
import { formatDateForInput } from '../../lib/utils'
import { formatInTimeZone } from 'date-fns-tz'

const OrderModal = ({ open, onClose, order: initialOrder, mode, refresh }) => {
  const { t } = useTranslation('order_modal')
  const [isSuccessSaved, setIsSuccessSaved] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [isErrorSave, setIsErrorSave] = useState(false)
  const [errorSaveMessage, setErrorSaveMessage] = useState('')
  const [isNotModifiable, setIsNotModifiable] = useState(false)
  const [timeFilterType, setTimeFilterType] = useState('exact')
  //definir la commande avec des valeurs par defaut
  const [order, setOrder] = useState({
    id: '',
    customerId: '',
    customerName: '',
    contactName: '',
    contactPhone: '',
    comment: '',
    constructionSiteId: '',
    siteCode: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryDate: '',
    deliveryTimeStart: '',
    deliveryTimeEnd: '',
    orderDeliveryId: '',
    materials: [],
    rubbles: [],
  })
  const {
    data: clients,
    error: clientsError,
    loading: clientsLoading,
  } = useQuery(gqlClients.CLIENTS)
  const {
    data: constructionSites,
    error: sitesError,
    loading: sitesLoading,
    refetch: refetch_site,
  } = useQuery(gqlConstructionSite.CONSTRUCTION_SITES)
  const {
    data: user,
    error: userError,
    loading: userLoading,
  } = useQuery(gqlUsers.USER, {
    variables: {
      id: getUserFromResponse({ access_token: localStorage.getItem('access_token') }).id,
    },
  })
  const {
    data: materials,
    error: materialsError,
    loading: materialsLoading,
  } = useQuery(gqlMaterials.MATERIALS)
  const {
    data: rubbles,
    error: rubblesError,
    loading: rubblesLoading,
  } = useQuery(gqlMaterials.RUBBLE)
  const [createCommand] = useMutation(gqlCommand.CREATE_COMMAND)
  const [updateCommand] = useMutation(gqlCommand.UPDATE_COMMAND)
  const [createCommandMaterial] = useMutation(gqlCommand.CREATE_COMMAND_MATERIAL)
  const [deleteCommandMaterial] = useMutation(gqlCommand.DELETE_COMMAND_MATERIAL)
  const [updateCommandMaterial] = useMutation(gqlCommand.UPDATE_COMMAND_MATERIAL)
  const [createConstructionSite] = useMutation(gqlConstructionSite.CREATE_CONSTRUCTION_SITE)
  const [updateConstructionSite] = useMutation(gqlConstructionSite.UPDATE_CONSTRUCTION_SITE)
  const [createOrderDelivery] = useMutation(gqlOrderDelivery.CREATE_ORDER_DELIVERY)
  const [updateOrderDelivery] = useMutation(gqlOrderDelivery.UPDATE_ORDER_DELIVERY)
  const [materialsToDelete, setMaterialsToDelete] = useState([])

  const {
    data: orderDeliveryData,
  } = useQuery(gqlOrderDelivery.ORDER_DELIVERIES_BY_COMMAND_ID, {
    variables: { commandId: initialOrder?.id, first: 1 },
    skip: !initialOrder?.id || mode === MODES.CREATE,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    // si cas edition ou duplication récupération des informations de commande
    //=> pour remplir les champs par défaut
    if (initialOrder) {
      const formattedInput = formatDateForInput(initialOrder.dateDelivery)
      const [initDate, initTime] = formattedInput ? formattedInput.split('T') : ['', '']
      setOrder({
        id: initialOrder.id || '',
        customerId: initialOrder.customerId || '',
        customerName: initialOrder.customerName || '',
        contactName: initialOrder.contactName || '',
        contactPhone: initialOrder.contactPhone || '',
        comment: initialOrder.comment || '',
        constructionSiteId: initialOrder.constructionSiteId || '',
        siteCode: initialOrder.siteCode || '',
        deliveryAddress: initialOrder.deliveryAddress || '',
        deliveryCity: initialOrder.deliveryCity || '',
        deliveryDate: initDate,
        deliveryTimeStart: initTime,
        deliveryTimeEnd: initTime,
        materials:
          initialOrder.materials.filter((material) => material.family === 'MATERIAL') || [],
        rubbles: initialOrder.materials.filter((material) => material.family === 'RUBBLE') || [],
        createdBy: initialOrder.createdBy || '',
        createdDate: initialOrder.createdDate || '',
        orderNumber: initialOrder.orderNumber || '',
        status: mode === MODES.EDIT ? initialOrder.status : STATUSES.INCOMPLETE, // si mode edition status courant , si non Incomplete
      })
      const isNotModifiable =
        initialOrder.status === STATUSES.CANCELED || initialOrder.status === STATUSES.DONE
      setIsNotModifiable(isNotModifiable)
    }
  }, [initialOrder])

  useEffect(() => {
    if (orderDeliveryData?.orderDeliveries?.edges?.length > 0) {
      const od = orderDeliveryData.orderDeliveries.edges[0].node
      setOrder((prev) => ({
        ...prev,
        deliveryTimeStart: od.deliveryTimeStart || prev.deliveryTimeStart,
        deliveryTimeEnd: od.deliveryTimeEnd || prev.deliveryTimeEnd,
        orderDeliveryId: od.id,
      }))
      if (od.deliveryTimeStart && od.deliveryTimeEnd && od.deliveryTimeStart !== od.deliveryTimeEnd) {
        setTimeFilterType('range')
      }
    }
  }, [orderDeliveryData])

  const [addressOptions, setAddressOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const addressTimerRef = useRef(null)
  const cityTimerRef = useRef(null)
  const [searchAddresses] = useLazyQuery(gqlAddressAutoFill.SEARCH_ADDRESSES, { fetchPolicy: 'network-only' })
  const [searchCities] = useLazyQuery(gqlAddressAutoFill.SEARCH_CITIES, { fetchPolicy: 'network-only' })

  const loading =
    clientsLoading || sitesLoading || userLoading || materialsLoading || rubblesLoading
  const error =
    clientsError || sitesError || userError || materialsError || rubblesError

  //Permet d'attendre le chargement de toutes les données depuis graphql
  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  //Permet d'afficher au besoin un message d'erreur lors de la remonté des données depuis graphql
  if (error) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography color="error">{t('errorLoadingData')}</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  let availableCustomers =
    clients?.clients.edges.map(({ node }) => ({
      id: node.id,
      displayName: node.displayName,
      internalId: node.internalId,
      active: node.active,
    })) || []

  let availableSiteCodes =
    constructionSites?.constructionSites.nodes.map(({ id, address, city, code, postcode }) => ({
      address,
      city,
      code,
      postcode,
      id,
    })) || []

  let availableMaterials =
    materials?.productCategories.nodes.flatMap((category) =>
      category.materialsByIdProductCategory.nodes.map((material) => ({
        id: material.id,
        description: material.description,
        detail: material.detail,
        baseMeasureUnitCode: material.baseMeasureUnitCode,
        internalId: material.internalId,
        unit: material.measureUnitByBaseMeasureUnitCode.abbr,
      }))
    ) || []

  let availableRubbles =
    rubbles?.productCategories.nodes.flatMap((category) =>
      category.materialsByIdProductCategory.nodes.map((material) => ({
        id: material.id,
        description: material.description,
        detail: material.detail,
        baseMeasureUnitCode: material.baseMeasureUnitCode,
        internalId: material.internalId,
        unit: material.measureUnitByBaseMeasureUnitCode.abbr,
      }))
    ) || []

  const handleChange = (e) => {
    const { name, value } = e.target
    setOrder((prevOrder) => ({
      ...prevOrder,
      [name]: value,
    }))
  }
  const handleTimeFilterTypeChange = (type) => {
    setTimeFilterType(type)
    if (type === 'exact') {
      setOrder((prevOrder) => ({
        ...prevOrder,
        deliveryTimeEnd: prevOrder.deliveryTimeStart,
      }))
    }
  }

  // Ajout d'une ligne matériaux ou déblais avec des infos vides
  const handleAddMaterials = (type) => {
    if (type !== 'materials' && type !== 'rubbles') {
      //@toDo gestion des erreur saisie matériaux
      console.error('Invalid type specified:', type)
      return
    }
    setOrder((prevOrder) => ({
      ...prevOrder,
      [type]: [
        ...prevOrder[type],
        { internalId: '', quantity: '', unit: '', id: '', materialId: '' },
      ],
    }))
  }

  const handleMaterialsChange = (index, field, value, type) => {
    const newMaterials = [...order[type]]
    newMaterials[index][field] = value ?? ''
    if (field === 'name') {
      const availableList = type === 'materials' ? availableMaterials : availableRubbles
      const selectedMaterial = availableList.find((material) => material.internalId === value)
      if (selectedMaterial) {
        newMaterials[index]['unit'] = selectedMaterial.unit
        newMaterials[index]['materialId'] = selectedMaterial.id
        newMaterials[index]['internalId'] = selectedMaterial.internalId
      } else {
        newMaterials[index]['unit'] = ''
        newMaterials[index]['materialId'] = ''
        newMaterials[index]['internalId'] = ''
      }
    }
    setOrder((prevOrder) => ({
      ...prevOrder,
      [type]: newMaterials,
    }))
  }

  // supprimer toute la ligne matériaux/déblais à l'index 'index'
  const handleRemoveMaterials = async (index, type) => {
    const newMaterials = [...order[type]]
    //on récupère l'index du matériaux à supprimer avant qu'il ne soit plus dans la liste
    if (newMaterials[index].id !== '') {
      setMaterialsToDelete((prev) => [...prev, newMaterials[index].id])
    }
    newMaterials.splice(index, 1)
    setOrder((prevOrder) => ({
      ...prevOrder,
      [type]: newMaterials,
    }))
  }

  //modifier status
  // fonction pour modifier le status de la commande dans le select
  const handleStatusChange = (event) => {
    const newStatus = event.target.value
    setOrder((prevOrder) => ({
      ...prevOrder,
      status: newStatus,
    }))
  }

  /**fonction renvoie message de succes ou d'erreur de sauvegarde en fonction
   * du mode creation , edition ou duplication
   * cas erreur ou success
   */
  const getResultMessage = (mode, result) => {
    return t(`${result}.${mode}`)
  }

  const handleSave = async () => {
    try {
      if (materialsToDelete.length > 0) {
        for (const materialId of materialsToDelete) {
          await deleteCommandMaterial({
            variables: { id: materialId },
          })
        }
        setMaterialsToDelete([])
      }
      const isComplete =
        order.contactPhone &&
        order.deliveryAddress &&
        order.deliveryCity &&
        order.deliveryDate &&
        order.deliveryTimeStart &&
        (order.materials.length > 0 || order.rubbles.length > 0)

      let updatedOrder = { ...order }
      if (
        isComplete &&
        updatedOrder.status !== STATUSES.PLANIFIED &&
        updatedOrder.status !== STATUSES.DONE &&
        updatedOrder.status !== STATUSES.CANCELED
      ) {
        updatedOrder.status = STATUSES.COMPLETE
      }

      const originalDeliveryAddress = updatedOrder.deliveryAddress
      const originalDeliveryCity = updatedOrder.deliveryCity

      let newConstructionSiteId = updatedOrder.constructionSiteId
      const matchingSite = availableSiteCodes.find((site) => site.code === updatedOrder.siteCode)

      if (!matchingSite && updatedOrder.siteCode !== '') {
        const { data } = await createConstructionSite({
          variables: {
            code: updatedOrder.siteCode,
            address: updatedOrder.deliveryAddress,
            city: updatedOrder.deliveryCity,
            postcode: '',
          },
        })
        newConstructionSiteId = data.createConstructionSite.constructionSite.id
        updatedOrder.deliveryAddress = null
        updatedOrder.deliveryCity = null
      } else if (
        matchingSite &&
        (updatedOrder.deliveryAddress !== matchingSite.address ||
          updatedOrder.deliveryCity !== matchingSite.city)
      ) {
        await updateConstructionSite({
          variables: {
            id: matchingSite.id,
            address: updatedOrder.deliveryAddress,
            city: updatedOrder.deliveryCity,
            postcode: matchingSite.postcode,
          },
        })
        updatedOrder.deliveryAddress = null
        updatedOrder.deliveryCity = null
      }

      const combinedDate = updatedOrder.deliveryDate && updatedOrder.deliveryTimeStart
        ? `${updatedOrder.deliveryDate}T${updatedOrder.deliveryTimeStart}`
        : ''
      updatedOrder.dateDelivery = combinedDate
        ? formatInTimeZone(
            new Date(combinedDate),
            'Europe/Paris',
            "yyyy-MM-dd'T'HH:mm:ssXXX"
          )
        : null

      if (mode === MODES.CREATE || mode === MODES.DUPLICATE) {
        const { data } = await createCommand({
          variables: {
            clientId: updatedOrder.customerId.length < 32 ? null : updatedOrder.customerId,
            clientName: updatedOrder.customerName,
            createdBy: user.user.id,
            comment: updatedOrder.comment || null,
            constructionSiteId: newConstructionSiteId === '' ? null : newConstructionSiteId,
            address: updatedOrder.deliveryAddress || null,
            city: updatedOrder.deliveryCity || null,
            contactName: updatedOrder.contactName || null,
            contactPhone: updatedOrder.contactPhone || null,
            dateDelivery: updatedOrder.dateDelivery || null,
            status: updatedOrder.status,
          },
        })
        const commandId = data.createCommand.command.id

        for (const material of updatedOrder.materials) {
          await createCommandMaterial({
            variables: {
              commandId,
              materialId: material.materialId,
              quantity: +material.quantity,
            },
          })
        }

        for (const rubble of updatedOrder.rubbles) {
          await createCommandMaterial({
            variables: {
              commandId,
              materialId: rubble.materialId,
              quantity: +rubble.quantity,
            },
          })
        }

        await createOrderDelivery({
          variables: {
            commandId,
            deliveryDate: updatedOrder.deliveryDate,
            deliveryTimeStart: updatedOrder.deliveryTimeStart,
            deliveryTimeEnd: updatedOrder.deliveryTimeEnd || updatedOrder.deliveryTimeStart,
            status: STATUSES.INCOMPLETE,
            deliverySiteAddress: originalDeliveryAddress,
            deliverySiteCity: originalDeliveryCity,
          },
        })
      } else if (mode === MODES.EDIT) {
        const materialsToUpdate = updatedOrder.materials.filter((material) => material.id !== '')
        for (const material of materialsToUpdate) {
          await updateCommandMaterial({
            variables: {
              id: material.id,
              materialId: material.materialId,
              quantity: +material.quantity,
            },
          })
        }

        const rubblesToUpdate = updatedOrder.rubbles.filter((material) => material.id !== '')
        for (const material of rubblesToUpdate) {
          await updateCommandMaterial({
            variables: {
              id: material.id,
              materialId: material.materialId,
              quantity: +material.quantity,
            },
          })
        }

        const materialsToCreate = updatedOrder.materials.filter((material) => material.id === '')
        for (const material of materialsToCreate) {
          await createCommandMaterial({
            variables: {
              commandId: updatedOrder.id,
              materialId: material.materialId,
              quantity: +material.quantity,
            },
          })
        }

        const rubblesToCreate = updatedOrder.rubbles.filter((material) => material.id === '')
        for (const material of rubblesToCreate) {
          await createCommandMaterial({
            variables: {
              commandId: updatedOrder.id,
              materialId: material.materialId,
              quantity: +material.quantity,
            },
          })
        }

        await updateCommand({
          variables: {
            address: updatedOrder.deliveryAddress,
            city: updatedOrder.deliveryCity,
            clientId: updatedOrder.customerId.length < 32 ? null : updatedOrder.customerId,
            clientName: updatedOrder.customerName,
            contactName: updatedOrder.contactName,
            contactPhone: updatedOrder.contactPhone,
            comment: updatedOrder.comment,
            constructionSiteId: newConstructionSiteId.length < 32 ? null : newConstructionSiteId,
            dateDelivery: updatedOrder.dateDelivery || null,
            status: updatedOrder.status,
            id: updatedOrder.id,
          },
        })

        if (updatedOrder.orderDeliveryId) {
          await updateOrderDelivery({
            variables: {
              id: updatedOrder.orderDeliveryId,
              deliveryDate: updatedOrder.deliveryDate,
              deliveryTimeStart: updatedOrder.deliveryTimeStart,
              deliveryTimeEnd: updatedOrder.deliveryTimeEnd || updatedOrder.deliveryTimeStart,
              status: updatedOrder.status,
            },
          })
        }
      }

      setOrder(updatedOrder)
      refetch_site()
      setSuccessMessage(getResultMessage(mode, 'success'))
      setIsSuccessSaved(true)
      setTimeout(() => {
        setIsSuccessSaved(false)
        onClose()
        refresh()
      }, 1000)
    } catch (err) {
      setErrorSaveMessage(getResultMessage(mode, 'error'))
      setIsErrorSave(true)
      setTimeout(() => {
        setIsErrorSave(false)
        onClose()
      }, 1000)
    }
  }

  //Handler autocomplétion Client
  const handleAutocompleteChange = (event, newValue) => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      customerId: newValue ? newValue.id : '',
      customerName: newValue ? newValue.displayName : '',
    }))
  }

  // on change code chantier , remplir les infos d'adresse en fonction du code chantier
  const handleSiteCodeChange = (event, newValue) => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      constructionSiteId: newValue ? newValue.id : '',
      siteCode: newValue ? newValue.code : '',
      deliveryAddress: newValue ? newValue.address : '',
      deliveryCity: newValue ? newValue.city : '',
    }))
  }

  //Handler saisie Client
  const handleInputChange = (event, newInputValue) => {
    //on vérifie si le nom correspond à celui d'un client
    const matchedCustomer = availableCustomers.find(
      (customer) => customer.displayName === newInputValue
    )
    setOrder((prevOrder) => ({
      ...prevOrder,
      //si le nom du client est récupéré alors on renseigne son id sinon on laisse vide
      customerId: matchedCustomer ? matchedCustomer.id : '',
      customerName: newInputValue,
    }))
  }

  //Handler saisie code chantier
  const handleSiteCodeInputChange = (event, newInputValue) => {
    const matchedSiteCode = availableSiteCodes?.find((siteCode) => siteCode.code === newInputValue)
    setOrder((prevOrder) => ({
      ...prevOrder,
      constructionSiteId: matchedSiteCode ? matchedSiteCode.id : '',
      siteCode: newInputValue,
      deliveryAddress: matchedSiteCode ? matchedSiteCode.address : '',
      deliveryCity: matchedSiteCode ? matchedSiteCode.city : '',
    }))
  }

  // Fonction utilitaire pour extraire le numéro de rue et le nom de rue
  const parseAddress = (address) => {
    if (!address) return { number: '', street: '' }

    // Regex pour détecter un numéro en début de chaîne (avec éventuellement bis, ter, etc.)
    const match = address.match(/^(\d+(?:\s*(?:bis|ter|quater|quinquies))?)\s+(.+)$/i)

    if (match) {
      return {
        number: match[1].trim(),
        street: match[2].trim(),
      }
    }

    // Si pas de numéro détecté, tout est considéré comme le nom de rue
    return {
      number: '',
      street: address.trim(),
    }
  }

  // Fonction de filtrage personnalisée
  const customFilterOptions = (options, { inputValue }) => {
    const { street: inputStreet } = parseAddress(inputValue)

    return options.filter((option) => {
      const { street: optionStreet } = parseAddress(option)

      // Recherche dans le nom de rue uniquement (ignore les numéros)
      return optionStreet.toLowerCase().includes(inputStreet.toLowerCase())
    })
  }

  // Fonction pour formater l'affichage des options
  const formatOption = (option, inputValue) => {
    const { number: inputNumber } = parseAddress(inputValue)

    // Si l'utilisateur a saisi un numéro, on l'affiche avec l'option
    if (inputNumber) {
      return `${inputNumber} ${option}`
    }

    return option
  }

  const handleAddressSearch = (value) => {
    if (addressTimerRef.current) clearTimeout(addressTimerRef.current)
    if (!value || value.length < 2) {
      setAddressOptions([])
      return
    }
    addressTimerRef.current = setTimeout(async () => {
      const { street } = parseAddress(value)
      if (!street) return
      try {
        const { data } = await searchAddresses({ variables: { search: street.toUpperCase() } })
        const results = data?.addressAutoFills?.edges?.map(({ node }) => node.address) || []
        setAddressOptions(results)
      } catch {
        setAddressOptions([])
      }
    }, 300)
  }

  const handleCitySearch = (value) => {
    if (cityTimerRef.current) clearTimeout(cityTimerRef.current)
    if (!value || value.length < 2) {
      setCityOptions([])
      return
    }
    cityTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await searchCities({ variables: { search: value.toUpperCase() } })
        const results = data?.cityAutoFills?.edges?.map(({ node }) => node.city) || []
        setCityOptions(results)
      } catch {
        setCityOptions([])
      }
    }, 300)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={{ minWidth: 600 }}>
      <CustomDialogTitle>
        <div>
          <h5>
            {mode === MODES.CREATE
              ? t('addNewOrder')
              : mode === MODES.EDIT
              ? t('editOrder')
              : t('duplicateOrder')}
          </h5>
        </div>
        <Button
          style={{
            color: 'inherit',
            fontSize: '1rem',
          }}
          onClick={onClose}
        >
          X
        </Button>
      </CustomDialogTitle>
      <DialogContent className="little-margin-top">
        {/*affichage des messages d'erreur et de success*/}
        {isSuccessSaved && <Box className="success-message">{successMessage}</Box>}
        {isErrorSave && <Box className="error-message">{errorSaveMessage}</Box>}
        {/*partie informations générales à la commande , seulement en cas d'edition */}
        {mode === MODES.EDIT && (
          <Grid container spacing={2} className="little-margin-bottom">
            <Grid container item xs={7}>
              <Grid item xs={6}>
                <div className="centered-label">
                  <label className="centered-label" htmlFor="createdDate">
                    {' '}
                    {t('creationDate')}:{' '}
                  </label>
                  <span className="centered-span"> {order.createdDate}</span>
                </div>
                <div className="centered-label">
                  <label className="centered-label" htmlFor="createdBy">
                    {t('createdBy')} :{' '}
                  </label>
                  <span className="centered-span"> {order.createdBy}</span>
                </div>
                <div className="centered-label">
                  <label className="centered-label" htmlFor="orderNumber">
                    {t('orderNumber')} :{' '}
                  </label>
                  <span className="centered-span"> {order.orderNumber}</span>
                </div>
              </Grid>
              <Grid item xs={6} alignItems="right">
                <div className="centered-container">
                  <label className="centered-label">Status :</label>
                  <span className="centered-span">
                    {' '}
                    {order.status && (
                      <SelectStatus
                        status={order.status}
                        statuses={orderStatuses()}
                        onChange={(e) => handleStatusChange(e)}
                        isDisabled={isNotModifiable}
                      />
                    )}
                  </span>
                </div>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={2}>
          {/*partie gauche information client et contact */}
          <Grid item xs={6}>
            <Typography variant="h6">{t('customer')} </Typography>
            <Autocomplete
              className="little-margin-top"
              options={availableCustomers}
              value={order.customerName ? { displayName: order.customerName } : null}
              fullWidth
              margin="normal"
              getOptionLabel={
                (option) =>
                  option && option.internalId && option.displayName
                    ? `${option.internalId} - ${option.displayName}`
                    : order.customerName || '' // Fallback to customerName or an empty string
              }
              isOptionEqualToValue={(option, value) => option?.internalId === value?.internalId}
              onChange={handleAutocompleteChange} // Handle selection from autocomplete
              onInputChange={handleInputChange} // Handle manual input
              renderInput={(params) => (
                <AutocompleteTextField {...params} label={t('customerName')} />
              )}
              clearIcon={null} // Remove clear button
              freeSolo // Allow user to input text not in the list
              disabled={isNotModifiable}
              sx={{
                '& svg': { position: 'static' },
              }}
            />

            <Typography className="little-margin-top" variant="h6">
              {t('contact')}{' '}
            </Typography>
            <TextField
              label={t('contactName')}
              name="contactName"
              value={order.contactName}
              onChange={handleChange}
              fullWidth
              margin="normal"
              disabled={isNotModifiable}
            />
            <TextField
              label={t('phoneNumber')}
              name="contactPhone"
              value={order.contactPhone}
              onChange={(e) => {
                const value = e.target.value
                // Autorise uniquement les chiffres
                if (/^\d*$/.test(value)) {
                  handleChange(e)
                }
              }}
              fullWidth
              margin="normal"
              disabled={isNotModifiable}
            />

            <TextField
              label={t('comment')}
              name="comment"
              value={order.comment}
              onChange={handleChange}
              fullWidth
              margin="normal"
              multiline
              rows={4}
              disabled={isNotModifiable}
            />
          </Grid>
          {/*partie droite information code chantier , adresse et date de livraison */}
          <Grid item xs={6}>
            <Typography variant="h6">{t('delivery')} </Typography>
            <Autocomplete
              className="little-margin-top"
              options={availableSiteCodes}
              value={order.siteCode ? { code: order.siteCode } : null}
              getOptionLabel={(option) => (option ? option.code : order.siteCode)}
              onChange={handleSiteCodeChange} //Permet de gérer quand on choisit l'autocomplétion
              onInputChange={handleSiteCodeInputChange}
              renderInput={(params) => <AutocompleteTextField {...params} label={t('siteCode')} />}
              clearIcon={null}
              freeSolo
              disabled={isNotModifiable}
              sx={{
                '& svg': { position: 'static' },
              }}
            />

            <Autocomplete
              options={addressOptions}
              value={order.deliveryAddress || ''}
              getOptionLabel={(option) => option || ''}
              filterOptions={customFilterOptions}
              onChange={(event, newValue) => {
                if (newValue) {
                  const { number: inputNumber } = parseAddress(order.deliveryAddress || '')
                  const finalValue = inputNumber ? `${inputNumber} ${newValue}` : newValue
                  setOrder((prevOrder) => ({
                    ...prevOrder,
                    deliveryAddress: finalValue,
                  }))
                } else {
                  setOrder((prevOrder) => ({
                    ...prevOrder,
                    deliveryAddress: '',
                  }))
                }
              }}
              onInputChange={(event, newInputValue) => {
                setOrder((prevOrder) => ({
                  ...prevOrder,
                  deliveryAddress: newInputValue,
                }))
                handleAddressSearch(newInputValue)
              }}
              renderOption={(props, option, { inputValue }) => (
                <li {...props}>{formatOption(option, inputValue)}</li>
              )}
              renderInput={(params) => (
                <AutocompleteTextField {...params} label={t('deliveryAddress')} margin="normal" />
              )}
              clearIcon={null}
              fullWidth
              disabled={isNotModifiable}
              sx={{
                '& svg': { position: 'static' },
              }}
            />

            <Autocomplete
              options={cityOptions}
              value={order.deliveryCity || ''}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.city || '')}
              onChange={(event, newValue) => {
                setOrder((prevOrder) => ({
                  ...prevOrder,
                  deliveryCity: typeof newValue === 'string' ? newValue : newValue?.city || '',
                }))
              }}
              onInputChange={(event, newInputValue) => {
                setOrder((prevOrder) => ({
                  ...prevOrder,
                  deliveryCity: newInputValue,
                }))
                handleCitySearch(newInputValue)
              }}
              renderInput={(params) => (
                <AutocompleteTextField {...params} label={t('deliveryCity')} margin="normal" />
              )}
              clearIcon={null}
              fullWidth
              disabled={isNotModifiable}
              sx={{
                '& svg': { position: 'static' },
              }}
            />

            <DeliveryDateTimePicker
              deliveryDate={order.deliveryDate}
              deliveryTimeStart={order.deliveryTimeStart}
              deliveryTimeEnd={order.deliveryTimeEnd}
              timeFilterType={timeFilterType}
              onDeliveryDateChange={(value) =>
                setOrder((prevOrder) => ({ ...prevOrder, deliveryDate: value }))
              }
              onDeliveryTimeStartChange={(value) => {
                setOrder((prevOrder) => ({
                  ...prevOrder,
                  deliveryTimeStart: value,
                  deliveryTimeEnd: timeFilterType === 'exact' ? value : prevOrder.deliveryTimeEnd || value,
                }))
              }}
              onDeliveryTimeEndChange={(value) =>
                setOrder((prevOrder) => ({ ...prevOrder, deliveryTimeEnd: value }))
              }
              onTimeFilterTypeChange={handleTimeFilterTypeChange}
              isNotModifiable={isNotModifiable}
            />
          </Grid>
        </Grid>
        {/* partie materiaux */}
        <Grid className="little-margin-bottom">
          <MaterialsList
            materials={order.materials}
            availableMaterials={availableMaterials.map((material) => ({
              ...material,
              id: material.id,
              internalId: material.internalId,
              unit: material.unit,
            }))}
            handleAddMaterial={() => handleAddMaterials('materials')}
            handleMaterialChange={handleMaterialsChange}
            handleRemoveMaterial={handleRemoveMaterials}
            title={t('materials')}
            buttonTitle={t('addMaterial')}
            type="materials"
            isNotModifiable={isNotModifiable}
          />
        </Grid>

        {/* partie rubbles */}
        <Grid className="little-margin-top">
          <MaterialsList
            materials={order.rubbles}
            availableMaterials={availableRubbles.map((material) => ({
              ...material,
              id: material.id,
              internalId: material.internalId,
              unit: material.unit,
            }))}
            handleAddMaterial={() => handleAddMaterials('rubbles')}
            handleMaterialChange={handleMaterialsChange}
            handleRemoveMaterial={handleRemoveMaterials}
            title={t('rubbles')}
            buttonTitle={t('addRubble')}
            type="rubbles"
            isNotModifiable={isNotModifiable}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="warning">
          {t('cancel')}
        </Button>
        <Button
          disabled={!order.customerName?.trim() || isNotModifiable}
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

OrderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  mode: PropTypes.oneOf([MODES.CREATE, MODES.EDIT, MODES.DUPLICATE]).isRequired,
  refresh: PropTypes.func,
}

export default OrderModal